import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_COMMERCIAL_LIST_CONFIGURATION, EMPTY_COMMERCIAL_LIST_CRITERIA } from "../constant/commercialConstant";
import { Commercial } from "../objects/commercial";
import { CommercialListCriteria } from "../objects/commercialListCriteria";
import { CommercialState } from "../objects/commercialstate";
import { setCommercialPreference } from "../utils/setCommercialPreference";
import { EMPTY_TRIP_LIST_CONFIGURATION, EMPTY_TRIP_LIST_CRITERIA } from "../constant/tripConstants";
import { setCommercialTripListPreference } from "../utils/setCommercialTripListPreference";
import { CommercialTripListCriteria } from "../objects/commercialTripListCriteria";
import { Trip } from "../../trip/objects/trip";
import { CommercialInvoice } from "../objects/commercialInvoice";
import { Column } from "../objects/column";


const initialState: CommercialState = {
    commercial_list: [],
    commercial_list_configuration: EMPTY_COMMERCIAL_LIST_CONFIGURATION,
    commercial_list_loading: false,
    commercial_list_count: 0,
    commercial_list_criteria: EMPTY_COMMERCIAL_LIST_CRITERIA,
    commercial: undefined,
    commercial_search_agency: '',


    trip_list: [],
    trip_list_configuration: EMPTY_TRIP_LIST_CONFIGURATION,
    trip_list_loading: false,
    trip_list_count: 0,
    trip_list_criteria: EMPTY_TRIP_LIST_CRITERIA,


    invoice_credential: null,
    invoice_list: null,
    trip_open_id: null,

    first_preference_loaded: false
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');


const addCommercialListConfigurationPreferenceReducer = (state: CommercialState, action: PayloadAction<Preferences[]>): CommercialState => ({
    ...state, commercial_list_configuration: { ...state.commercial_list_configuration, preferences: [...state.commercial_list_configuration.preferences, ...action.payload] }
});

const setCommercialListConfigurationPreferenceReducer = (state: CommercialState, action: PayloadAction<Preferences[]>): CommercialState => ({
    ...state, commercial_list_configuration: { ...state.commercial_list_configuration, preferences: action.payload }
});

const setPreferenceReducer = (state: CommercialState, action: PayloadAction<Preferences[]>): CommercialState => {
    const { preferences, columns, order_by, order } = setCommercialPreference(action.payload, state.commercial_list_configuration.columns);
    const trip = setCommercialTripListPreference(action.payload, state.trip_list_configuration.columns);
    return {
        ...state,
        commercial_list_configuration: { ...state.commercial_list_configuration, preferences, columns },
        commercial_list_criteria: { ...state.commercial_list_criteria, order, order_by },
        trip_list_configuration: { ...state.trip_list_configuration, preferences: trip.preferences, columns: trip.columns },
        trip_list_criteria: { ...state.trip_list_criteria, order: trip.order, order_by: trip.order_by },
        first_preference_loaded: true
    };
};


const moveDownTripColumnReducer = (state: CommercialState, action: PayloadAction<number>): CommercialState => {
    const columns = cloneDeep([...state.trip_list_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload + 1] };
    columns[action.payload + 1] = columns[action.payload];
    columns[action.payload + 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, trip_list_configuration: { ...state.trip_list_configuration, columns } };
};

const moveUpTripColumnReducer = (state: CommercialState, action: PayloadAction<number>): CommercialState => {
    const columns = cloneDeep([...state.trip_list_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload - 1] };
    columns[action.payload - 1] = columns[action.payload];
    columns[action.payload - 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, trip_list_configuration: { ...state.trip_list_configuration, columns } };
};
const toggleTripColumnReducer = (state: CommercialState, action: PayloadAction<number>): CommercialState => {
    const columns = cloneDeep([...state.trip_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, trip_list_configuration: { ...state.trip_list_configuration, columns } };
};
const updateTripListReducer = (state: CommercialState, action: PayloadAction<Trip[]>): CommercialState => ({
    ...state, trip_list: action.payload
});


const moveDownColumnReducer = (state: CommercialState, action: PayloadAction<number>): CommercialState => {
    const columns = cloneDeep([...state.commercial_list_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload + 1] };
    columns[action.payload + 1] = columns[action.payload];
    columns[action.payload + 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, commercial_list_configuration: { ...state.commercial_list_configuration, columns } };
};


const moveUpColumnReducer = (state: CommercialState, action: PayloadAction<number>): CommercialState => {
    const columns = cloneDeep([...state.commercial_list_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload - 1] };
    columns[action.payload - 1] = columns[action.payload];
    columns[action.payload - 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, commercial_list_configuration: { ...state.commercial_list_configuration, columns } };
};

const toggleColumnReducer = (state: CommercialState, action: PayloadAction<number>): CommercialState => {
    const columns = cloneDeep([...state.commercial_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, commercial_list_configuration: { ...state.commercial_list_configuration, columns } };
};

const updateCommercialListReducer = (state: CommercialState, action: PayloadAction<Commercial[]>): CommercialState => ({
    ...state, commercial_list: action.payload
});
const updateCommercialListCriteriaReducer = (state: CommercialState, action: PayloadAction<CommercialListCriteria>): CommercialState => ({
    ...state, commercial_list_criteria: action.payload
});
const updateCommercialReducer = (state: CommercialState, action: PayloadAction<Commercial | undefined>): CommercialState => ({
    ...state, commercial: action.payload
});
const updateCommercialListLoadingReducer = (state: CommercialState, action: PayloadAction<boolean>): CommercialState => ({
    ...state, commercial_list_loading: action.payload
});
const updateCommercialListCountReducer = (state: CommercialState, action: PayloadAction<number>): CommercialState => ({
    ...state, commercial_list_count: action.payload
});
const updateTripListCriteriaReducer = (state: CommercialState, action: PayloadAction<CommercialTripListCriteria>): CommercialState => ({
    ...state, trip_list_criteria: action.payload
});
const updateTripListLoadingReducer = (state: CommercialState, action: PayloadAction<boolean>): CommercialState => ({
    ...state, trip_list_loading: action.payload
});
const updateTripListCountReducer = (state: CommercialState, action: PayloadAction<number>): CommercialState => ({
    ...state, trip_list_count: action.payload
});
const addTripListConfigurationPreferenceReducer = (state: CommercialState, action: PayloadAction<Preferences[]>): CommercialState => ({
    ...state, trip_list_configuration: { ...state.trip_list_configuration, preferences: [...state.trip_list_configuration.preferences, ...action.payload] }
});
const setTripListConfigurationPreferenceReducer = (state: CommercialState, action: PayloadAction<Preferences[]>): CommercialState => ({
    ...state, trip_list_configuration: { ...state.trip_list_configuration, preferences: action.payload }
});
const setInvoiceCredentialReducer = (state: CommercialState, action: PayloadAction<{ trip: Trip, invoice: CommercialInvoice } | null>) => ({
    ...state, invoice_credential: action.payload
});

const setInvoiceListReducer = (state: CommercialState, action: PayloadAction<CommercialInvoice[] | null>): CommercialState => ({
    ...state, invoice_list: action.payload, trip_open_id: action.payload ? state.trip_open_id : null
});
const updateInvoiceReducer = (state: CommercialState, action: PayloadAction<CommercialInvoice>): CommercialState => ({
    ...state, invoice_list: state.invoice_list?.map((invoice) => invoice.id === action.payload.id ? action.payload : invoice) || null
});
const setTripOpenIdReducer = (state: CommercialState, action: PayloadAction<number | null>): CommercialState => ({
    ...state, trip_open_id: action.payload
});
const setColumnsReducer = (state: CommercialState, action: PayloadAction<Column[]>): CommercialState => ({
    ...state, commercial_list_configuration: { ...state.commercial_list_configuration, columns: action.payload },
});
const setTripColumnsReducer = (state: CommercialState, action: PayloadAction<Column[]>): CommercialState => ({
    ...state, trip_list_configuration: { ...state.trip_list_configuration, columns: action.payload },
});
const setCommercialAgencySearchReducer = (state: CommercialState, action: PayloadAction<string>): CommercialState => ({
    ...state, commercial_search_agency: action.payload
});
const resizeCommercialColumnReducer = (state: CommercialState, action: PayloadAction<ColumnResizePayload>): CommercialState => ({
    ...state, commercial_list_configuration: { ...state.commercial_list_configuration, columns: applyColumnResize(state.commercial_list_configuration.columns, action.payload) },
});
const resizeTripColumnReducer = (state: CommercialState, action: PayloadAction<ColumnResizePayload>): CommercialState => ({
    ...state, trip_list_configuration: { ...state.trip_list_configuration, columns: applyColumnResize(state.trip_list_configuration.columns, action.payload) },
});

export const commercial = createSlice({
    name: 'commercial',
    initialState,
    reducers: {
        addCommercialListConfigurationPreference: addCommercialListConfigurationPreferenceReducer,
        setCommercialListConfigurationPreference: setCommercialListConfigurationPreferenceReducer,
        moveDownColumn: moveDownColumnReducer,
        moveUpColumn: moveUpColumnReducer,
        toggleColumn: toggleColumnReducer,
        updateCommercialList: updateCommercialListReducer,
        updateCommercialListCriteria: updateCommercialListCriteriaReducer,
        updateCommercial: updateCommercialReducer,
        updateCommercialListLoading: updateCommercialListLoadingReducer,
        updateCommercialListCount: updateCommercialListCountReducer,
        moveDownTripColumn: moveDownTripColumnReducer,
        moveUpTripColumn: moveUpTripColumnReducer,
        toggleTripColumn: toggleTripColumnReducer,
        updateTripList: updateTripListReducer,
        updateTripListCriteria: updateTripListCriteriaReducer,
        updateTripListLoading: updateTripListLoadingReducer,
        updateTripListCount: updateTripListCountReducer,
        addTripListConfigurationPreference: addTripListConfigurationPreferenceReducer,
        setTripListConfigurationPreference: setTripListConfigurationPreferenceReducer,
        setInvoiceCredential: setInvoiceCredentialReducer,
        setInvoiceList: setInvoiceListReducer,
        updateInvoice: updateInvoiceReducer,
        setTripOpenId: setTripOpenIdReducer,
        setColumns: setColumnsReducer,
        setTripColumns: setTripColumnsReducer,
        setCommercialAgencySearch: setCommercialAgencySearchReducer,
        resizeCommercialColumn: resizeCommercialColumnReducer,
        resizeTripColumn: resizeTripColumnReducer,
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    addCommercialListConfigurationPreference,
    setCommercialListConfigurationPreference,
    setColumns,
    toggleColumn,
    updateCommercialList,
    updateCommercialListCriteria,
    updateCommercial,
    updateCommercialListLoading,
    updateCommercialListCount,
    moveDownTripColumn,
    moveUpTripColumn,
    setTripColumns,
    toggleTripColumn,
    updateTripList,
    updateTripListCriteria,
    updateTripListLoading,
    updateTripListCount,
    addTripListConfigurationPreference,
    setTripListConfigurationPreference,
    setInvoiceCredential,
    setInvoiceList,
    updateInvoice,
    setTripOpenId,
    setCommercialAgencySearch,
    resizeCommercialColumn,
    resizeTripColumn,
} = commercial.actions;
